import React, { useState, useRef, useEffect } from "react"
import { useSelector } from "react-redux"
import PropTypes from "prop-types"
import { Button, Row, Col, Typography, Flex } from "antd"
import { useTranslation } from "react-i18next"
import { InfoCircleOutlined } from "@ant-design/icons"
import styles from "./ExperienceBooking.module.css"
import { colors } from "themes/variables"

const { Text } = Typography

const GuestsSelector = ({ counts, setCounts, activeTab, error, setBookingErrors, maxGuests, minGuests }) => {
  const {
    experienceBookingData: { bookingSelections }
  } = useSelector((state) => state.experiences)

  const [t] = useTranslation()
  const [open, setOpen] = useState(false)
  const containerRef = useRef(null)
  const { children_allowed, infants_allowed, passengers_allowed, extra_guests_allowed } = bookingSelections?.tripLength?.price || {}

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const updateCount = (section, key, delta) => {
    const newCounts = {
      ...counts,
      [section]: {
        ...counts?.[section],
        [key]: Math.max(0, counts?.[section]?.[key] + delta)
      }
    }
    setCounts(newCounts)

    const total =
      Object.values(newCounts?.main).reduce((sum, v) => sum + v, 0) + Object.values(newCounts?.extra).reduce((sum, v) => sum + v, 0)

    if (total > 0 && setBookingErrors) {
      setBookingErrors((prev) => ({ ...prev, counts: false }))
    }
  }

  // Initialize counts with default values when component mounts
  useEffect(() => {
    const hasNoGuests = !counts?.main?.adults && !counts?.main?.children && !counts?.main?.infants && !counts?.main?.passengers
    if (minGuests > 0 && hasNoGuests) {
      setCounts({
        main: { adults: minGuests || 0, children: 0, infants: 0, passengers: 0 },
        extra: { adults: 0, children: 0, infants: 0, passengers: 0 }
      })
    }
  }, [minGuests, setCounts])

  const clearCounts = () => {
    setCounts({
      main: { adults: 0, children: 0, infants: 0, passengers: 0 },
      extra: { adults: 0, children: 0, infants: 0, passengers: 0 }
    })
  }

  const getGuestsSummary = () => {
    const labels = { adults: "Adult", children: "Child", infants: "Infant", passengers: "Passenger" }

    const format = (section) => {
      const sectionCounts = counts?.[section] || {}
      return Object.entries(sectionCounts)
        .filter(([_, count]) => count > 0)
        .map(([key, count]) => `${count} ${labels?.[key]}${count > 1 ? "s" : ""}`)
        .join(", ")
    }

    return [format("main"), extra_guests_allowed ? format("extra") : ""].filter(Boolean).join(" , ")
  }

  const getGuestTotals = (counts) => {
    const mainCounts = counts?.main || {}
    const extraCounts = counts?.extra || {}

    const totalMain = Object.values(mainCounts).reduce((sum, val) => sum + (val || 0), 0)
    const totalExtra = Object.values(extraCounts).reduce((sum, val) => sum + (val || 0), 0)

    return { main: totalMain, extra: totalExtra, total: totalExtra + totalMain }
  }

  const renderGuestBlock = (sectionLabel, sectionKey, maxLimit) => (
    <>
      {sectionLabel && (
        <div style={{ marginBottom: "10px" }}>
          <Text style={{ fontWeight: 600 }}>{sectionLabel}</Text>
        </div>
      )}
      {[
        { label: "Adults", key: "adults", allowed: true },
        { label: "Children (< 12 years)", key: "children", allowed: children_allowed },
        { label: "Infants (< 2 years)", key: "infants", allowed: infants_allowed },
        {
          label: (
            <>
              Passengers <InfoCircleOutlined style={{ fontSize: 12, color: "#1890ff" }} />
            </>
          ),
          key: "passengers",
          allowed: passengers_allowed
        }
      ].map(({ label, key, allowed }) => (
        <Row key={sectionKey + key} justify="space-between" align="middle" style={{ marginBottom: 10 }}>
          <Col>{label}</Col>
          <Col>
            <Button
              size="middle"
              onClick={(e) => {
                e.stopPropagation()
                updateCount(sectionKey, key, -1)
              }}
              disabled={!allowed || counts?.[sectionKey]?.[key] === 0}
            >
              -
            </Button>
            <Text style={{ margin: "0 12px" }}>{counts?.[sectionKey]?.[key]}</Text>
            <Button
              size="middle"
              onClick={(e) => {
                e.stopPropagation()
                updateCount(sectionKey, key, 1)
              }}
              disabled={!allowed || maxLimit}
            >
              +
            </Button>
          </Col>
        </Row>
      ))}
    </>
  )

  return (
    <div className={styles.guestsWrapper} ref={containerRef}>
      <Flex
        className={`${styles.inputContainer} ${open ? styles.activeBorder : ""} ${error ? styles.errorBorder : ""}`}
        onClick={() => {
          if (activeTab !== 2 && activeTab !== 3) setOpen(!open)
        }}
      >
        <span className={styles.inputLabel}>Guests</span>
        <button
          className={getGuestTotals(counts)?.total > 0 ? styles.selectedInputLink : styles.inputLink}
          style={{ color: activeTab === 1 ? colors.primary : colors.textSecondary }}
          onClick={() => {
            if (activeTab !== 2 && activeTab !== 3) setOpen(!open)
          }}
        >
          {getGuestsSummary() || "Add Guests"}
        </button>
      </Flex>
      {error && <Text style={{ color: "red" }}>{t("webIntegration.embedded_validation.guest")}</Text>}
      {open && (
        <div className={styles.dropdownPanel}>
          {renderGuestBlock("", "main", getGuestTotals(counts)?.main >= maxGuests)}
          {extra_guests_allowed &&
            getGuestTotals(counts)?.main >= maxGuests &&
            renderGuestBlock(
              "Extra Guests",
              "extra",
              getGuestTotals(counts)?.extra >= bookingSelections?.tripLength?.price?.max_extra_guests
            )}
          <Button
            type="link"
            onClick={(e) => {
              e.stopPropagation()
              clearCounts()
            }}
          >
            Clear
          </Button>
        </div>
      )}
    </div>
  )
}

GuestsSelector.propTypes = {
  counts: PropTypes.shape({
    main: PropTypes.object,
    extra: PropTypes.object
  }).isRequired,
  setCounts: PropTypes.func.isRequired,
  activeTab: PropTypes.number,
  error: PropTypes.bool,
  setBookingErrors: PropTypes.func,
  maxGuests: PropTypes.number,
  minGuests: PropTypes.number
}

export default React.memo(GuestsSelector)
